// src/pages/app/deals/index.tsx
import { useRef, useState, useMemo } from 'react';
import type { GridApi } from '@ag-grid-community/core';
import { Archive, LayoutGrid, RotateCcw, Table2, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AGDataGrid from '@/components/ag-data-grid';
import Container from '@/components/container';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useGrid } from '@/contexts/grid-context';
import { useDeals, useUpdateDeal } from '@/hooks/use-deals';
import type { DealStage, DealOutcome, DealType } from '@/hooks/use-deals';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { DealColumns } from './components/deal-columns';
import { dealColumnDefs } from './components/deal-grid-columns';

const DEAL_STAGES: DealStage[] = [
  'Annual Verification',
  'Pending Verification',
  'Organization Verification in Progress',
  'Name Selection in Progress',
  'Employment Verification in Progress',
  'WHOIS Update in Progress',
  'Failed Verification',
  'Hold',
  'Verification Completed',
  'Verified Data Sent',
  'Completed',
  'Migrated',
];

const DEFAULT_SELECTED_STAGES = DEAL_STAGES.filter((stage) => stage !== 'Migrated');

const DEAL_OUTCOMES: DealOutcome[] = [
  'Approved',
  'Combined',
  'Consolidation',
  'Did not Register',
  'Domain Deleted',
  'Duplicate',
  'fTLD Domain',
  'Incomplete',
  'Ineligible',
  'Junk',
  'Name Selection',
  'No Renew',
  'No Reply',
  'No Verification Needed',
  'Not Wanted',
  'Organization',
  'Price',
  'Test',
  'Migrated',
];

const DEFAULT_SELECTED_OUTCOMES = DEAL_OUTCOMES.filter((outcome) => outcome !== 'Migrated');

// Deal type quick filters for all views
const DEAL_TYPES: DealType[] = ['New Business', 'Existing Business', 'Annual Verification', 'Forced Verification'];
const DEFAULT_SELECTED_TYPES: DealType[] = [];

type ViewMode = 'grid' | 'columns';

function DealsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [viewMode, setViewMode] = usePersistedState<ViewMode>('deals-view-mode', 'grid');
  const [selectedStages, setSelectedStages] = usePersistedState<DealStage[]>('deals-selected-stages', DEFAULT_SELECTED_STAGES);
  const [selectedOutcomes, setSelectedOutcomes] = usePersistedState<DealOutcome[]>('deals-selected-outcomes', DEFAULT_SELECTED_OUTCOMES);
  const [selectedDealTypes, setSelectedDealTypes] = usePersistedState<DealType[]>('deals-selected-types', DEFAULT_SELECTED_TYPES);
  const [isFiltersOpen, setIsFiltersOpen] = usePersistedState('deals-filters-open', true);
  const gridApiRef = useRef<GridApi | null>(null);
  const { registerGridApi } = useGrid();

  const { data: deals, isLoading, error, isFetching } = useDeals();
  const updateDeal = useUpdateDeal();

  const handleSelectionChanged = (event: { api: { getSelectedRows: () => any[] } }) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const handleArchive = async () => {
    try {
      await Promise.all(
        selectedRows.map(async (row) =>
          updateDeal.mutateAsync({
            id: row.id,
            archived_at: new Date().toISOString(),
          }),
        ),
      );
      toast.success('Selected deals archived successfully');
      setSelectedRows([]);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const handleGridReady = (params: { api: GridApi }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
  };

  const clearFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
      setSearchQuery('');
      setSelectedStages(DEFAULT_SELECTED_STAGES);
      setSelectedOutcomes(DEFAULT_SELECTED_OUTCOMES);
      setSelectedDealTypes(DEFAULT_SELECTED_TYPES);
    }
  };

  const filteredDeals = useMemo(() => {
    if (!deals)
      return [];

    return deals.filter((deal) => {
      // Stage and outcome filters
      const stageMatch = selectedStages.includes(deal.deal_stage);
      const outcomeMatch = !deal.outcome || selectedOutcomes.includes(deal.outcome);
      const typeMatch = selectedDealTypes.length === 0 || selectedDealTypes.includes(deal.deal_type);

      // Search query filter
      if (!searchQuery)
        return stageMatch && outcomeMatch && typeMatch;

      const query = searchQuery.toLowerCase();
      const searchMatch = deal.domains.some((domain) => domain.toLowerCase().includes(query))
        || deal.deal_stage.toLowerCase().includes(query);

      return stageMatch && outcomeMatch && typeMatch && searchMatch;
    });
  }, [deals, searchQuery, selectedStages, selectedOutcomes, selectedDealTypes]);

  // Build column defs to ensure only one sort is active at a time (no sort order numbers).
  const dynamicColumnDefs = useMemo(() => {
    const defs = dealColumnDefs.map((def) => ({ ...def }));
    const createdCol = defs.find((d) => d.field === 'created_at');
    const updatedCol = defs.find((d) => d.field === 'updated_at');

    if (createdCol) {
      delete (createdCol as any).sort;
      delete (createdCol as any).sortIndex;
    }
    if (updatedCol) {
      delete (updatedCol as any).sort;
      delete (updatedCol as any).sortIndex;
    }

    if (selectedDealTypes.length > 0) {
      if (updatedCol) {
        (updatedCol as any).sort = 'asc'; // oldest first
      }
    } else {
      if (createdCol) {
        (createdCol as any).sort = 'desc'; // newest first
      }
    }

    return defs;
  }, [selectedDealTypes]);

  // Sorting handled via dynamicColumnDefs to avoid GridApi type issues

  if (error) {
    toast.error('Failed to load deals');
  }

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-3 flex justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold">All Deals</h1>
          {isFetching && !isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              <span>Fetching latest deals...</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(value) => value && setViewMode(value as ViewMode)}
            aria-label="View mode"
          >
            <ToggleGroupItem
              value="grid"
              aria-label="Grid view"
              className="px-3"
            >
              <Table2 className="size-4" />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="columns"
              aria-label="Column view"
              className="px-3"
            >
              <LayoutGrid className="size-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          {selectedRows.length > 0 && (
            <Button
              variant="outline"
              onClick={handleArchive}
              disabled={updateDeal.isPending}
              className="gap-2"
            >
              <Archive className="size-4" />
              Archive Selected
            </Button>
          )}
        </div>
      </div>
      <div className="mb-6 flex gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search deals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={clearFilters}
          className="gap-2"
        >
          <RotateCcw className="size-4" />
          Clear Filters
        </Button>
      </div>

      <Collapsible
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        className="mb-6 space-y-2"
      >
        <div className="flex items-center gap-2">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <ChevronDown className={`size-4 transition-transform ${isFiltersOpen ? 'rotate-180' : ''}`} />
              Filters
            </Button>
          </CollapsibleTrigger>
          <div className="text-sm text-muted-foreground">
            {selectedStages.length}
            {' '}
            stages and
            {' '}
            {selectedOutcomes.length}
            {' '}
            outcomes selected
          </div>
        </div>

        <CollapsibleContent className="space-y-4">
          <div className="flex flex-wrap items-start gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Deal Types</Label>
                <Checkbox
                  checked={selectedDealTypes.length === DEAL_TYPES.length}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedDealTypes([...DEAL_TYPES]);
                    } else {
                      setSelectedDealTypes([]);
                    }
                  }}
                />
                <Label className="text-xs text-muted-foreground">All</Label>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                {DEAL_TYPES.map((type) => (
                  <div key={type} className="flex items-center gap-2">
                    <Checkbox
                      id={`type-${type}`}
                      checked={selectedDealTypes.includes(type)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedDealTypes([...selectedDealTypes, type]);
                        } else {
                          setSelectedDealTypes(selectedDealTypes.filter((t) => t !== type));
                        }
                      }}
                    />
                    <Label htmlFor={`type-${type}`} className="text-sm">{type}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Deal Stages</Label>
                <Checkbox
                  checked={selectedStages.length === DEAL_STAGES.length}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedStages([...DEAL_STAGES]);
                    } else {
                      setSelectedStages([]);
                    }
                  }}
                />
                <Label className="text-xs text-muted-foreground">All</Label>
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                {DEAL_STAGES.map((stage) => (
                  <div key={stage} className="flex items-center gap-2">
                    <Checkbox
                      id={`stage-${stage}`}
                      checked={selectedStages.includes(stage)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedStages([...selectedStages, stage]);
                        } else {
                          setSelectedStages(selectedStages.filter((s) => s !== stage));
                        }
                      }}
                    />
                    <Label htmlFor={`stage-${stage}`} className="text-sm">{stage}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium">Deal Outcomes</Label>
                <Checkbox
                  checked={selectedOutcomes.length === DEAL_OUTCOMES.length}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedOutcomes([...DEAL_OUTCOMES]);
                    } else {
                      setSelectedOutcomes([]);
                    }
                  }}
                />
                <Label className="text-xs text-muted-foreground">All</Label>
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                {DEAL_OUTCOMES.map((outcome) => (
                  <div key={outcome} className="flex items-center gap-2">
                    <Checkbox
                      id={`outcome-${outcome}`}
                      checked={selectedOutcomes.includes(outcome)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedOutcomes([...selectedOutcomes, outcome]);
                        } else {
                          setSelectedOutcomes(selectedOutcomes.filter((o) => o !== outcome));
                        }
                      }}
                    />
                    <Label htmlFor={`outcome-${outcome}`} className="text-sm">{outcome}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {viewMode === 'grid'
        ? (
            <div className={isLoading ? 'opacity-50' : ''}>
              <AGDataGrid
                columnDefs={dynamicColumnDefs}
                rowData={filteredDeals}
                quickFilterText={searchQuery}
                rowSelection={{ mode: 'multiRow' }}
                onGridReady={handleGridReady}
                onSelectionChanged={handleSelectionChanged}
                overlayLoadingTemplate={
                  isLoading ? '<span class="ag-overlay-loading-center">Loading...</span>' : undefined
                }
                overlayNoRowsTemplate={
                  isLoading ? '' : '<span class="ag-overlay-no-rows-center">No deals found</span>'
                }
              />
            </div>
          )
        : (
            <DealColumns deals={filteredDeals} />
          )}
    </Container>
  );
}

export default DealsPage;
